﻿import { useQuery } from '@tanstack/react-query';
import type { RoleCode } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { leaveService } from '@/services/leave.service';
import { attendanceService } from '@/services/attendance.service';
import { ClipboardCheck, LogIn } from 'lucide-react';

interface GreetingHeroProps {
  greeting: string;
  firstName: string;
  today: string;
  roles: RoleCode[];
  isAdminTier: boolean;
  isManagerTier: boolean;
}

function formatTime(isoStr: string | null | undefined): string {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function GreetingHero({
  greeting,
  firstName,
  today,
  roles,
  isAdminTier,
  isManagerTier,
}: GreetingHeroProps) {
  const { has } = usePermission();

  const pendingLeaveQ = useQuery({
    queryKey: ['dashboard', 'greeting', 'pending-leave'],
    queryFn: () => leaveService.listTeamRequests({ status: 'pending', page: 1, limit: 1 }),
    enabled: isAdminTier || isManagerTier,
  });

  const todayStatusQ = useQuery({
    queryKey: ['dashboard', 'greeting', 'today-status'],
    queryFn: () => attendanceService.getTodayStatus(),
    enabled: !isAdminTier && !isManagerTier && has(PERMISSIONS.ATTENDANCE_PUNCH),
  });

  function getSummaryChip(): React.ReactNode {
    if (isAdminTier || isManagerTier) {
      const pending = pendingLeaveQ.data?.pagination?.total ?? 0;
      return (
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 opacity-80" />
          <span>
            {pending > 0 ? `${pending} approval${pending === 1 ? '' : 's'} pending` : 'No pending approvals'}
          </span>
        </div>
      );
    }

    // Latest punch decides — the daily record's last_punch_out stays set after a re-check-in.
    const punches = todayStatusQ.data?.punches ?? [];
    const lastPunch = punches[punches.length - 1];

    return (
      <div className="flex items-center gap-2">
        <LogIn className="h-4 w-4 opacity-80" />
        <span>
          {lastPunch?.punch_type === 'check_in'
            ? `Checked in at ${formatTime(lastPunch.punch_time)}`
            : lastPunch?.punch_type === 'check_out'
              ? `Checked out at ${formatTime(lastPunch.punch_time)}`
              : 'Not checked in yet'}
        </span>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 to-teal-800 p-5 text-white shadow-lg sm:p-6">
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
      <div className="absolute -bottom-4 left-1/3 h-24 w-24 rounded-full bg-teal-400/20 blur-xl" />
      <div className="absolute right-1/4 top-1/2 h-16 w-16 rounded-full bg-white/5 blur-lg" />

      <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-teal-100">{today}</p>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {greeting}, {firstName}
          </h1>
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {roles.length > 0 ? (
              roles.map((r) => (
                <span
                  key={r}
                  className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium"
                >
                  {r.replace(/_/g, ' ')}
                </span>
              ))
            ) : (
              <span className="text-xs text-teal-200">No roles assigned</span>
            )}
          </div>
        </div>

        <div className="min-w-[160px] self-start rounded-xl bg-white/15 px-4 py-3 text-sm font-medium backdrop-blur-sm">
          {getSummaryChip()}
        </div>
      </div>
    </div>
  );
}
